import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, Grid, IconButton, Tooltip,
  ToggleButtonGroup, ToggleButton, TextField, InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { orderQualityCheckService } from '../services/api';
import QCResultDrawer from '../components/QCResultDrawer';
import { format, parseISO } from 'date-fns';
import { uk } from 'date-fns/locale';

const COLUMNS = [
  { key: 'new', label: 'Новий', color: '#1565c0', bg: '#e3f2fd' },
  { key: 'repeat', label: 'Повторний', color: '#e65100', bg: '#fff3e0' },
  { key: 'done', label: 'Виконано', color: '#2e7d32', bg: '#e8f5e9' },
];

function fmtDate(val) {
  if (!val) return '—';
  try { return format(parseISO(val), 'dd.MM.yyyy HH:mm', { locale: uk }); }
  catch { return val; }
}

function QCCard({ item, colColor, colBg, onClick }) {
  return (
    <Paper
      elevation={2}
      onClick={() => onClick(item)}
      sx={{
        p: 1.5, mb: 1.5, cursor: 'pointer', borderLeft: `4px solid ${colColor}`,
        '&:hover': { boxShadow: 4 },
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" noWrap>
        {item.order_product_name || '—'}
      </Typography>
      {item.order_product_article && (
        <Typography variant="caption" color="text.secondary">{item.order_product_article}</Typography>
      )}
      <Typography variant="body2" mt={0.5}>
        Верстат: <b>{item.order_machine_identifier || '—'}</b>
      </Typography>
      {item.order_customer_order_number && (
        <Typography variant="body2">Замовлення: {item.order_customer_order_number}</Typography>
      )}
      <Typography variant="body2">
        Виготовлено: {item.order_produced_fact ?? 0} / {item.order_quantity ?? 0} шт
      </Typography>
      <Typography variant="body2">
        План до: {fmtDate(item.order_planned_end)}
      </Typography>
      {item.status === 'repeat' && item.checked_at && (
        <Typography variant="caption" color="warning.dark">
          Попередня перевірка: {fmtDate(item.checked_at)}
        </Typography>
      )}
      <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
        <Chip
          label={item.status_display || item.status}
          size="small"
          sx={{ bgcolor: colBg, color: colColor, fontWeight: 600 }}
        />
        {item.check_location === 'warehouse' && (
          <Chip label="Склад ГП" size="small" sx={{ bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 600 }} />
        )}
      </Box>
    </Paper>
  );
}

export default function QCKanbanPage({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = useMemo(() => {
    const h = searchParams.get('highlight');
    if (h != null && /^\d+$/.test(String(h).trim())) return Number(h.trim());
    return null;
  }, [searchParams]);
  const highlightOrderId = useMemo(() => {
    const o = searchParams.get('order');
    if (o != null && /^\d+$/.test(String(o).trim())) return Number(o.trim());
    return null;
  }, [searchParams]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await orderQualityCheckService.getAll();
      setItems(res.data.results || res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openDrawer(item) {
    setSelected(item);
    setDrawerOpen(true);
  }

  useEffect(() => {
    if (loading || !items.length) return;
    if (highlightId == null && highlightOrderId == null) return;
    const hit = items.find((it) => (
      (highlightId != null && Number(it.id) === highlightId)
      || (highlightOrderId != null && Number(it.order) === highlightOrderId && it.status !== 'done')
    ));
    if (!hit) return;
    openDrawer(hit);
    const next = new URLSearchParams(searchParams);
    next.delete('highlight');
    next.delete('order');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items, highlightId, highlightOrderId]);

  const filteredItems = items.filter((item) => {
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        (item.order_product_name || '').toLowerCase().includes(q)
        || (item.order_product_article || '').toLowerCase().includes(q)
        || (item.order_machine_identifier || '').toLowerCase().includes(q)
        || String(item.order_customer_order_number || '').toLowerCase().includes(q)
        || String(item.order || '').includes(q)
      );
    }
    return true;
  });

  const byStatus = {};
  COLUMNS.forEach((c) => { byStatus[c.key] = []; });
  filteredItems.forEach((i) => { if (byStatus[i.status]) byStatus[i.status].push(i); });

  const visibleCols = statusFilter === 'all' ? COLUMNS : COLUMNS.filter((c) => c.key === statusFilter);

  return (
    <Box p={embedded ? 0 : 2}>
      <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Пошук по виробу, верстату, замовленню..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => { if (v) setStatusFilter(v); }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5, fontSize: 12 } }}
        >
          <ToggleButton value="all">Всі</ToggleButton>
          <ToggleButton value="new" sx={{ color: '#1565c0', '&.Mui-selected': { bgcolor: '#e3f2fd', color: '#1565c0' } }}>Новий</ToggleButton>
          <ToggleButton value="repeat" sx={{ color: '#e65100', '&.Mui-selected': { bgcolor: '#fff3e0', color: '#e65100' } }}>Повторний</ToggleButton>
          <ToggleButton value="done" sx={{ color: '#2e7d32', '&.Mui-selected': { bgcolor: '#e8f5e9', color: '#2e7d32' } }}>Виконано</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Оновити">
          <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
        {loading && <CircularProgress size={20} />}
      </Box>

      <Grid container spacing={2}>
        {visibleCols.map((col) => (
          <Grid item xs={12} md={statusFilter === 'all' ? 4 : 12} key={col.key}>
            <Box sx={{ bgcolor: col.bg, borderRadius: 2, p: 1.5, minHeight: 400 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography fontWeight="bold" color={col.color}>{col.label}</Typography>
                <Chip label={byStatus[col.key].length} size="small" sx={{ bgcolor: col.color, color: '#fff' }} />
              </Box>
              {byStatus[col.key].length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center" mt={4}>
                  Немає записів
                </Typography>
              )}
              {byStatus[col.key].map((item) => (
                <QCCard
                  key={item.id}
                  item={item}
                  colColor={col.color}
                  colBg={col.bg}
                  onClick={openDrawer}
                />
              ))}
            </Box>
          </Grid>
        ))}
      </Grid>

      <QCResultDrawer
        open={drawerOpen}
        item={selected}
        onClose={() => setDrawerOpen(false)}
        onDone={() => load()}
      />
    </Box>
  );
}
